import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, Minus, TrendingUp } from "lucide-react";
import {
  ECONOMICS_PAYOFF,
  ECONOMICS_PILLARS,
  ECONOMICS_PROOF_STRIP,
  ECONOMICS_SCAN_LINE,
  ECONOMICS_STAMP,
} from "./economicsContent";

const FlowArrow = ({ vertical = false }) => (
  <div
    className={`flex items-center justify-center text-orange-500/40 ${
      vertical ? "py-2" : "px-1 sm:px-2"
    }`}
    aria-hidden
  >
    {vertical ? (
      <div className="flex flex-col items-center gap-0.5">
        <div className="w-px h-3 bg-gradient-to-b from-transparent to-orange-500/50" />
        <ArrowRight className="w-4 h-4 rotate-90 text-orange-400/70" />
        <div className="w-px h-3 bg-gradient-to-b from-orange-500/50 to-transparent" />
      </div>
    ) : (
      <div className="flex items-center gap-0.5">
        <div className="w-4 sm:w-8 h-px bg-gradient-to-r from-transparent to-orange-500/50" />
        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400/70 flex-shrink-0" />
        <div className="w-4 sm:w-8 h-px bg-gradient-to-r from-orange-500/50 to-transparent" />
      </div>
    )}
  </div>
);

const PillarCell = ({ pillar, index }) => {
  const isHero = pillar.tone === "hero";

  return (
    <div
      className={`relative flex-1 min-w-0 ${
        isHero ? "sm:-mt-2 sm:mb-[-0.5rem]" : ""
      }`}
      data-testid={`economics-pillar-${index}`}
    >
      {isHero && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 badge-mono text-[9px] text-orange-400 bg-[#0a0e14] border border-orange-500/35 px-2.5 py-0.5 rounded-full whitespace-nowrap z-10">
          KEY OUTCOME
        </span>
      )}

      <div
        className={`h-full rounded-xl border text-center transition-shadow ${
          isHero
            ? "border-orange-500/45 bg-gradient-to-b from-orange-500/20 via-orange-500/8 to-transparent ring-1 ring-orange-500/25 shadow-lg shadow-orange-500/10 py-7 sm:py-9 px-4 sm:px-5"
            : "border-white/[0.08] bg-white/[0.02] py-6 sm:py-7 px-4 sm:px-5"
        }`}
      >
        <div className="flex items-baseline justify-center gap-0.5 mb-1">
          <span
            className={`font-mono font-bold leading-none tracking-tight ${
              isHero
                ? "text-5xl sm:text-6xl lg:text-7xl text-orange-400"
                : pillar.id === "headcount"
                  ? "text-4xl sm:text-5xl lg:text-6xl text-teal-400"
                  : "text-4xl sm:text-5xl lg:text-6xl text-white"
            }`}
          >
            {pillar.value}
          </span>
          {pillar.suffix && (
            <span className="text-xl sm:text-2xl font-mono text-gray-500">{pillar.suffix}</span>
          )}
        </div>

        <p
          className={`font-heading font-bold uppercase tracking-wide mb-2 ${
            isHero ? "text-sm sm:text-base text-orange-200" : "text-xs sm:text-sm text-gray-300"
          }`}
        >
          {pillar.label}
        </p>

        <p className="text-[11px] sm:text-xs text-gray-500 font-body leading-relaxed max-w-[12rem] mx-auto">
          {pillar.detail}
        </p>

        {pillar.id === "spend" && (
          <div className="flex items-center justify-center gap-1.5 mt-3 text-gray-600">
            <Minus className="w-3 h-3" />
            <span className="text-[10px] font-mono tracking-wider">FLAT</span>
          </div>
        )}
        {isHero && (
          <div className="flex items-center justify-center gap-1.5 mt-3 text-orange-400/80">
            <TrendingUp className="w-3.5 h-3.5" />
            <span className="text-[10px] font-mono tracking-wider">UP</span>
          </div>
        )}
        {pillar.id === "headcount" && (
          <div className="flex items-center justify-center gap-1.5 mt-3 text-teal-400/70">
            <span className="text-[10px] font-mono tracking-wider">NO PAYROLL</span>
          </div>
        )}
      </div>
    </div>
  );
};

const EconomicsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 overflow-hidden"
      data-testid="economics-section"
      ref={ref}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.04) 39px, rgba(255,255,255,0.04) 40px)",
        }}
      />
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-orange-500/30 to-transparent hidden lg:block" />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-6 lg:gap-8">
          <div className="lg:w-36 flex-shrink-0 flex lg:flex-col justify-between lg:justify-center gap-4">
            <p className="badge-mono text-[10px] sm:text-xs font-bold tracking-[0.3em] text-orange-400/90 uppercase">
              {ECONOMICS_STAMP}
            </p>
            <p className="hidden lg:block text-[10px] font-mono text-gray-600 leading-relaxed [writing-mode:vertical-rl] rotate-180 self-center tracking-widest uppercase">
              Input → Output → Headcount
            </p>
          </div>

          <div className="flex-1 min-w-0">
            <p
              className="text-lg sm:text-xl lg:text-2xl font-heading font-bold text-white leading-snug mb-6 sm:mb-8 max-w-3xl"
              data-testid="economics-scan-line"
            >
              <span className="text-gray-400">Same </span>
              <span className="text-white">Ad Spend</span>
              <span className="text-gray-500"> · </span>
              <span className="text-orange-400">More Booked Inspections</span>
              <span className="text-gray-500"> · </span>
              <span className="text-teal-400">Zero New Hires</span>
            </p>

            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={reduceMotion || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
              transition={{ duration: 0.45 }}
              className="rounded-2xl border border-dashed border-orange-500/25 bg-[#0a0e14]/80 p-4 sm:p-6 lg:p-8"
              data-testid="economics-equation-board"
            >
              <p className="text-[10px] font-mono text-gray-600 tracking-widest mb-4 sm:mb-6 text-center sm:text-left">
                {ECONOMICS_SCAN_LINE}
              </p>

              <div className="flex flex-col sm:flex-row sm:items-stretch gap-0 sm:gap-0">
                {ECONOMICS_PILLARS.map((pillar, index) => (
                  <React.Fragment key={pillar.id}>
                    <PillarCell pillar={pillar} index={index} />
                    {index < ECONOMICS_PILLARS.length - 1 && (
                      <>
                        <div className="sm:hidden">
                          <FlowArrow vertical />
                        </div>
                        <div className="hidden sm:flex items-center self-center">
                          <FlowArrow />
                        </div>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>

            <p className="mt-6 sm:mt-8 text-sm sm:text-base text-gray-400 font-body leading-relaxed max-w-3xl">
              <span className="text-white font-semibold">{ECONOMICS_PAYOFF.lead}</span>{" "}
              <span className="text-orange-300/90 font-medium">{ECONOMICS_PAYOFF.middle}</span>{" "}
              {ECONOMICS_PAYOFF.close}
            </p>

            <div
              className="mt-5 flex flex-col sm:flex-row rounded-xl overflow-hidden border border-white/[0.06] divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]"
              data-testid="economics-proof-strip"
            >
              {ECONOMICS_PROOF_STRIP.map((item) => (
                <div
                  key={item.tag}
                  className="flex-1 flex items-center gap-3 px-4 py-3 sm:py-3.5 bg-white/[0.015]"
                >
                  <span className="badge-mono text-[10px] font-bold text-orange-400/90 tracking-wider flex-shrink-0">
                    {item.tag}
                  </span>
                  <span className="text-[11px] sm:text-xs text-gray-500 font-body leading-snug">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EconomicsSection;
